import { Box, Grid2, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import MarkdownIt from "markdown-it";
import React, { useEffect, useState } from "react";
import MdEditor from "react-markdown-editor-lite";
import { useParams } from "react-router-dom";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { MESApiService, dxtToLocalServerTime } from "../../../../../services";
import { EntrySubmissionData } from "../../hooks/useEntrySubmission";
import { getEntryAttachments } from "../../repository/LogbookRepository";
import { AttachmentModal } from "../attachments/AttachmentModal";
import AttachmentPlugin from "../attachments/AttachmentPlugin";

type Props = {
	value: string | null;
	onChange: (content: string) => void;
	isEditing: boolean;
	isNewEntry: boolean;
	UpdateTimestamp: Date;
	UpdatedBy: string;
	entryId: number;
	entryData: EntrySubmissionData;
	onEntrySubmitted?: (entryId: number, entryData?: any) => void;
	allowAttachments?: boolean;
};

export const ContentInput = ({
	value,
	onChange,
	isEditing,
	UpdateTimestamp,
	UpdatedBy,
	isNewEntry,
	entryId,
	entryData,
	onEntrySubmitted,
	allowAttachments = false,
}: Props) => {
	const mdParser = new MarkdownIt();
	const api = new MESApiService();
	const { entryId: urlEntryId } = useParams();

	const [key, setKey] = useState(0);
	const [error, setError] = useState<string | null>(null);
	const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);

	// Use URL entryId as fallback when form entryId is not available yet
	const effectiveEntryId =
		entryId ||
		(urlEntryId && urlEntryId !== "new" ? parseInt(urlEntryId) : null);

	// Query for fetching attachment count
	const { data: attachments = [], error: attachmentError } = useQuery({
		queryKey: ["entryAttachments", effectiveEntryId],
		queryFn: ({ signal }) =>
			getEntryAttachments({ entryId: effectiveEntryId! }, signal),
		enabled: !!effectiveEntryId && effectiveEntryId > 0,
		retry: 1,
		onError: (err: Error) => {
			console.error("Failed to fetch attachments:", err);
		},
	});

	const attachmentCount = attachments.length;

	const handleChange = ({ text }: { text: string }) => {
		onChange(text);
	};

	const onImageUpload = async (files: File[]) => {
		try {
			setError(null);
			const res = await api.uploadFiles(files, "logbook");
			if (res) {
				return res.result[0]?.URL;
			}
		} catch (error) {
			setError("Failed to upload files");
			return "";
		}
	};

	useEffect(() => {
		setKey((prevKey) => prevKey + 1);
	}, [isEditing, attachmentCount]);

	// Register the attachment plugin
	useEffect(() => {
		MdEditor.use(AttachmentPlugin, {
			attachmentCount,
			onAttachmentClick: () => {
				if (entryData.title && entryData.author) {
					setAttachmentModalOpen(true);
				} else {
					setError("Title and Author are required");
				}
			},
		});
	}, [attachmentCount, entryData.title, entryData.author]);

	return (
		<>
			<Box
				sx={{
					height: "100%",
					width: "100%",
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
					"& .MuiInput-underline:before": {
						borderBottom: "none",
					},
					"& .MuiInput-underline:after": {
						borderBottom: "none",
					},
					"& .MuiInput-underline:hover:not(.Mui-disabled):before": {
						borderBottom: "none",
					},
				}}
			>
				<Grid2
					sx={{
						flexGrow: 1,
						display: "flex",
						flexDirection: "column",
						overflow: "hidden",
					}}
				>
					<Box
						sx={{
							height: "100%",
							"& .rc-md-editor": {
								height: "100%",
							},
							"& .rc-md-navigation": {
								backgroundColor: (theme) => theme.palette.background.paper,
							},
							"& .rc-md-editor, & .section-container": {
								height: "100%",
							},
						}}
					>
						<MdEditor
							key={key}
							style={{ height: "100%", width: "100%" }}
							value={value || ""}
							renderHTML={(text) => mdParser.render(text || "")}
							onChange={handleChange}
							view={{
								menu: allowAttachments ? true : isEditing, // Always show menu to display attachment button (if allowed)
								md: isEditing ? true : false,
								html: !isEditing,
							}}
							plugins={
								isEditing
									? [
											"header",
											"font-bold",
											"font-italic",
											"font-underline",
											"font-strikethrough",
											"list-unordered",
											"list-ordered",
											"block-quote",
											"block-wrap",
											"block-code-inline",
											"block-code-block",
											"table",
											"image",
											"link",
											"clear",
											"logger",
											"mode-toggle",
											"full-screen",
											"tab-insert",
											allowAttachments ? "attachment" : "",
										]
									: allowAttachments
										? [
												"attachment", // Only show attachment button in preview mode and with attachments allowed
											]
										: []
							}
							config={{
								attachment: allowAttachments
									? {
											attachmentCount,
											onAttachmentClick: () => {
												if (entryData.title && entryData.author) {
													setAttachmentModalOpen(true);
												} else {
													setError("Title and Author are required");
												}
											},
										}
									: null,
							}}
							autoFocus={!isNewEntry}
							onImageUpload={async (file: File) => {
								const markdownImage = await onImageUpload([file]);
								return markdownImage;
							}}
						/>
					</Box>
				</Grid2>

				{UpdatedBy?.length > 0 && (
					<Box
						sx={{
							backgroundColor: (theme) => theme.palette.background.paper,
							borderTop: (theme) => `0px solid ${theme.palette.divider}`,
							paddingLeft: 10,
						}}
					>
						<TextField
							variant="standard"
							disabled
							value={`This entry was updated on ${
								dxtToLocalServerTime(
									UpdateTimestamp,
									"MM/dd/yy hh:mm a",
								) as string
							} by ${UpdatedBy}`}
							fullWidth
							margin="none"
							autoComplete="off"
							InputProps={{
								style: {
									fontSize: "0.7rem",
								},
								disableUnderline: true,
							}}
							InputLabelProps={{
								style: {
									fontSize: "0.7rem",
								},
							}}
						/>
					</Box>
				)}
			</Box>
			<AttachmentModal
				open={attachmentModalOpen}
				onClose={() => setAttachmentModalOpen(false)}
				entryId={effectiveEntryId}
				isEditing={isEditing}
				entryData={entryData}
				onEntrySubmitted={onEntrySubmitted}
			/>
			<ErrorModal
				error={error || (attachmentError as Error)?.message}
				onHide={() => setError("")}
			/>
		</>
	);
};
